<script lang="ts" setup>
import type { MerchantShopRenewInfoVO } from '@/api/business/types/table'
import { useVModel } from '@vueuse/core'
import dayjs from 'dayjs'
import { isNil } from 'lodash-es'
import { onMounted, ref } from 'vue'
import { getRenewInfo } from '@/api/business/table'
import BuyQrCodeDialog from '@/components/dialog/BuyQrCodeDialog.vue'
import { getEnv } from '@/utils'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
}>()

const renewalItems = ref<MerchantShopRenewInfoVO[]>([])
const showBuyDialog = ref(false)
const qrCodeUrl = ref('')
const searchKeyword = ref('')
const dialogVisible = useVModel(props, 'show', emit)

const handleClose = () => {
  dialogVisible.value = false
}

// 获取续费信息
async function fetchRenewInfo() {
  try {
    const { data } = await getRenewInfo()
    if (data) {
      renewalItems.value = data.filter(item => {
        const delFlag = item.productType === 2 && isNil((item.expirationDate))
        return !delFlag
      })
    }
  } catch (error) {
    console.error('获取续费信息失败:', error)
  }
}

onMounted(() => {
  fetchRenewInfo()
})

// 根据模块状态返回按钮类型
function getButtonType(item: MerchantShopRenewInfoVO) {
  switch (item.productType) {
    case 1:
    case 2:
      return item.expirationDate ? 'renew' : 'activate'
    case 3:
    case 4:
      return 'buy'
  }
}

const getRemainText = (item: MerchantShopRenewInfoVO) => {
  const daysRemaining = dayjs(item.expirationDate).diff(dayjs(), 'days')
  if (daysRemaining >= 0) {
    return `剩余${daysRemaining}天`
  } else {
    return '剩余0天'
  }
}

const showIntroduction = (item: MerchantShopRenewInfoVO) => {
  if (item.productType === 3) {
    return item.remainingUsageNumber <= 0
  }
  if (item.productType === 4) {
    return item.messageNumber <= 0
  }
  return item.introduction && !item.expirationDate
}

const handleRenew = (item: MerchantShopRenewInfoVO) => {
  qrCodeUrl.value = `https://api-dev.teahouses.cn/teashop_merchant/to_spu/${getEnv()}?spuId=${item.productId}`
  searchKeyword.value = item.productName
  showBuyDialog.value = true
}
</script>

<template>
  <el-dialog
    title="系统续费"
    v-model="dialogVisible"
    @close="handleClose"
    :append-to-body="true"
    width="560px"
  >
    <div class="renewal-content">
      <div
        v-for="(item, index) in renewalItems"
        :key="index"
        class="renewal-item"
      >
        <div class="item-left">
          <el-image
            style="width: 60px; height: 60px;"
            :src="item.productImage || undefined"
            fit="contain"
          >
            <template #placeholder>
              <div class="image-slot">暂无图片</div>
            </template>
          </el-image>
          <div class="item-info">
            <span class="item-name">{{ item.productName }}</span>
            <span v-if="showIntroduction(item)" class="item-desc">{{ item.introduction }}</span>
            <template v-if="[1, 2].includes(item.productType)">
              <span v-if="item.expirationDate" class="item-date">
                {{ item.expirationDate }}到期
                <span v-if="item.expirationDate" class="item-days">{{ getRemainText(item) }}</span>
              </span>
            </template>
            <template v-else>
              <span v-if="item.messageNumber" class="item-date">
                剩余可用短信数{{ item.messageNumber }}条
              </span>
              <span v-if="item.usageNumber" class="item-date">
                可创建员工账号数{{ item.usageNumber }}，剩余{{ item.remainingUsageNumber }}
              </span>
            </template>
          </div>
        </div>
        <el-button
          :type="getButtonType(item) === 'renew' ? 'primary' : 'default'"
          :plain="getButtonType(item) !== 'renew'"
          size="small"
          style=" width: 96px;height: 40px;font-size: 16px; border-radius: 20px"
          @click="handleRenew(item)"
        >
          {{ getButtonType(item) === 'renew' ? '立即续费' : getButtonType(item) === 'activate' ? '去开通' : '去购买' }}
        </el-button>
      </div>
    </div>
  </el-dialog>
  <!-- 扫码购买 -->
  <BuyQrCodeDialog
    v-model:show="showBuyDialog"
    :qr-code-url="qrCodeUrl"
    :search-keyword="searchKeyword"
  />
</template>

<style scoped>
.renewal-content {
  padding: 10px;
}

.renewal-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 58px;
  margin-bottom: 20px;
}

.item-left {
  display: flex;
  align-items: center;
}

.item-info {
  display: flex;
  flex-direction: column;
  margin-left: 10px;
}

.item-name {
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: bold;
}

.item-date,
.item-desc {
  margin-bottom: 5px;
  font-size: 14px;
  color: #828282;
}

.item-days {
  margin-left: 5px;
}
</style>
